import React, { useEffect, useState, useCallback } from 'react';
import { X, Loader2, Plus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CategoryManager = ({ onClose, onChanged }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    fetch(`${API_URL}/api/categories`)
      .then((r) => r.json())
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to add category');
      toast.success(`Category "${data.name}" added`);
      setNewName('');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (c) => {
    const name = window.prompt('New name for this category:', c.name);
    if (!name || name.trim() === c.name) return;
    try {
      const res = await fetch(`${API_URL}/api/categories/${c.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to rename');
      toast.success('Category renamed');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/categories/${c.id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to delete');
      toast.success('Category deleted');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto" data-testid="category-manager">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-lg font-serif font-bold text-[#001B2A]">Manage Categories</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="category-manager-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleAdd} className="p-5 flex gap-2 border-b border-gray-100">
          <input
            data-testid="new-category-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New category (e.g. Pocket Squares)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7B1E2B]"
          />
          <button
            type="submit"
            data-testid="add-category-button"
            disabled={saving}
            className="flex items-center gap-1 px-4 py-2 bg-[#7B1E2B] text-white rounded-md text-sm font-semibold hover:bg-[#001B2A] transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
          </button>
        </form>

        <div className="p-5 space-y-2">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[#7B1E2B]" /></div>
          ) : (
            categories.map((c) => (
              <div key={c.id} className="flex items-center gap-2 border border-gray-100 rounded-md px-3 py-2" data-testid={`category-row-${c.slug}`}>
                <span className="flex-1 font-medium text-[#001B2A]">{c.name}</span>
                <button data-testid={`rename-category-${c.slug}`} onClick={() => handleRename(c)} className="p-1.5 text-gray-500 hover:text-[#001B2A] hover:bg-gray-100 rounded">
                  <Pencil className="h-4 w-4" />
                </button>
                <button data-testid={`delete-category-${c.slug}`} onClick={() => handleDelete(c)} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
          <p className="text-xs text-gray-400 pt-2">
            New categories automatically appear in the site menu and homepage. A category can only be deleted when no products use it.
          </p>
        </div>
      </div>
    </div>
  );
};
